import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chip, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem, useDisclosure } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CardGrid, CtyButton, CtyShowToast, EntityCardWithActions, checkType } from '@circuitry-ai/doc-ui';
import { useTranslation } from 'react-i18next';
import { AGENT_BUILDER_URL, agentBuilderApi, useAgentBuilderAuth, type AgentSummary, type Blueprint } from './agentBuilderApi';
import { useAsjRecords } from './asjRecords';
import { AIWorkerList } from '../lazyload/lazyload';

// ASJs live in the Journeys section: listed on the Journeys page and built at JOURNEYS_PATH/asj/<name>.
export const JOURNEYS_PATH = '/aiworkers/list/journeys';
export const ASJ_PATH = `${JOURNEYS_PATH}/asj`;
const DEFAULT_MODEL = 'default';
const CUSTOM = '__custom__'; // the Create dialog's "Custom journey": no blueprint

// The journeys (ASJs) built by chat, as a section of the Journeys page, and the "Create ASJ"
// dialog: a name, a model and the blueprint it is built in. The type (ASJ) is implied.
// Each ASJ is also a DOC AI worker (specialist, sub_type asj; see asjRecords): the list shows the
// ASJs this tenant has a record for, plus any journey not added yet (made before records existed).
// basePath: where an ASJ opens (the Journeys area, or the temporary preview route).
// inGrid: the cards go straight into the surrounding card grid (the Journeys page's own list), so
// ASJs and the other journeys are one list under one search; search filters them by name.
export function AsjJourneys({
  basePath = ASJ_PATH,
  isCreateOpen,
  onCreateOpenChange,
  onCount,
  inGrid = false,
  search = '',
}: {
  basePath?: string;
  isCreateOpen: boolean;
  onCreateOpenChange: (open: boolean) => void;
  onCount?: (count: number) => void; // how many ASJs are listed (the page hides its empty message)
  inGrid?: boolean;
  search?: string;
}) {
  useAgentBuilderAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentSummary[] | null>(null);
  const [blueprints, setBlueprints] = useState<Blueprint[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const asj = useAsjRecords();

  const load = () => agentBuilderApi.agents().then(setAgents, (e: Error) => setError(e.message));
  useEffect(() => {
    void load();
    agentBuilderApi.blueprints().then(setBlueprints, () => undefined);
  }, []);
  const listed = asj.loaded ? (agents ?? []).filter((a) => asj.records[a.name]) : [];
  const unlisted = asj.loaded ? (agents ?? []).filter((a) => !asj.records[a.name] && a.editable) : [];
  useEffect(() => {
    if (agents && asj.loaded) onCount?.(listed.length + unlisted.length);
  }, [agents, asj.loaded, listed.length, unlisted.length, onCount]);

  // Gives a journey made before ASJs were DOC AI workers its record, so it is listed like the rest.
  const add = async (a: AgentSummary) => {
    setAdding(a.name);
    try {
      await asj.register(a.name, a.title, a.description);
      await asj.refetch();
      CtyShowToast(checkType.success, t('Added to workers.'));
    } catch (e) {
      CtyShowToast(checkType.error, (e as Error).message);
    } finally {
      setAdding(null);
    }
  };

  const remove = async (name: string, close: () => void) => {
    setDeleting(name);
    try {
      // The DOC record first: DOC decides who may delete it.
      await asj.remove(name);
      await agentBuilderApi.deleteAgent(name);
      CtyShowToast(checkType.success, t('Journey deleted.'));
      close();
      await Promise.all([load(), asj.refetch()]);
    } catch (e) {
      CtyShowToast(checkType.error, (e as Error).message);
    } finally {
      setDeleting(null);
    }
  };

  const titleOf = (id: string | null) => blueprints.find((b) => b.id === id)?.title;
  const query = search.trim().toLowerCase();
  const shown = [...listed, ...unlisted].filter((a) => !query || (a.title || a.name).toLowerCase().includes(query));
  // In the page's grid: one grid item per card (display: contents), and messages span the grid.
  const Cards = inGrid ? Fragment : CardGrid;
  const note = inGrid ? 'col-span-full text-sm' : 'text-sm';

  return (
    <section className={inGrid ? 'contents' : 'px-4 pt-2'} aria-label={t('Service journeys')} data-testid="agent-builder-list">
      {error && (
        <p className={`${note} text-danger`} title={AGENT_BUILDER_URL}>
          {t('The agent builder is unavailable right now. Please try again in a moment.')}
        </p>
      )}
      {(!agents || !asj.loaded) && !error && <p className={`${note} text-default-500`}>{t('Loading…')}</p>}
      {!inGrid && agents && asj.loaded && listed.length + unlisted.length === 0 && (
        <p className="text-sm text-default-500">{t('No service journeys yet. Use Create Journey to start one.')}</p>
      )}
      {/* The same cards as the Agents section: name, description, actions menu, open button. */}
      {/* Journeys made before ASJs had DOC records get the same card, with a small "Add to workers". */}
      <Cards>
        {shown.map((a) => (
          <div key={a.name} data-testid={`ab-agent-${a.name}`}>
            <EntityCardWithActions
              id={`asj-${a.name}`}
              name={a.title || a.name}
              description={a.description || (a.editable ? '' : t('Written by hand: runs as is, cannot be edited here.'))}
              bodyExtra={
                <div className="flex min-w-0 flex-nowrap items-center gap-1 overflow-hidden whitespace-nowrap">
                  {a.blueprint && (
                    <Chip size="sm" variant="bordered" className="shrink-0">
                      {titleOf(a.blueprint) ?? a.blueprint}
                    </Chip>
                  )}
                  <Chip size="sm" variant="flat" className="shrink-0" color={a.status === 'published' ? 'success' : 'warning'}>
                    {t(a.status)}
                  </Chip>
                  {!asj.records[a.name] && (
                    <CtyButton
                      size="sm"
                      variant="light"
                      color="primary"
                      className="h-6 min-w-0 shrink px-1 text-xs"
                      isLoading={adding === a.name}
                      onPress={() => void add(a)}
                      title={t('This journey is not one of the account’s AI workers yet. Add it so the account manages it like its other journeys.')}
                      id={`ab-add-${a.name}`}
                    >
                      {t('Add to workers')}
                    </CtyButton>
                  )}
                  <span className="ml-auto shrink-0 pl-1 text-xs text-default-400">{a.updated_at ? new Date(a.updated_at).toLocaleDateString() : ''}</span>
                </div>
              }
              footerItems={
                <CtyButton
                  className="w-full border-1"
                  variant="solid"
                  radius="sm"
                  size="lg"
                  color="primary"
                  isDisabled={!a.editable}
                  onPress={() => navigate(`${basePath}/${a.name}`)}
                  id={`ab-open-${a.name}`}
                >
                  {t('Open')}
                </CtyButton>
              }
              canDelete
              dropdownAriaLabel={t('Journey actions')}
              deleteModalEntityName={a.title || a.name}
              onConfirmDelete={(close) => void remove(a.name, close)}
              isDeleteLoading={deleting === a.name}
              deleteConfirmLabel={t('Delete')}
            />
          </div>
        ))}
      </Cards>
      <CreateAsjModal
        isOpen={isCreateOpen}
        onOpenChange={onCreateOpenChange}
        blueprints={blueprints}
        register={asj.register}
        onCreated={(name) => navigate(`${basePath}/${name}`)}
      />
    </section>
  );
}

export function CreateAsjButton({ onPress }: { onPress: () => void }) {
  const { t } = useTranslation();
  return (
    <CtyButton color="primary" variant="flat" onPress={onPress} id="ab-create-agent" startContent={<Icon icon="lucide:sparkles" width={16} />}>
      {t('Create ASJ')}
    </CtyButton>
  );
}

// The Journeys page: one list, under one search, of the account's journeys and its ASJs (the ASJ
// cards first). "Create Journey" creates an ASJ.
export function JourneysPage() {
  const dialog = useDisclosure();
  return (
    <AIWorkerList
      category="journeys"
      onCreate={dialog.onOpen}
      extraCards={(search: string) => (
        <AsjJourneys inGrid search={search} isCreateOpen={dialog.isOpen} onCreateOpenChange={dialog.onOpenChange} />
      )}
    />
  );
}

function CreateAsjModal({
  isOpen,
  onOpenChange,
  blueprints,
  register,
  onCreated,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  blueprints: Blueprint[];
  register: (asjName: string, title: string, description: string) => Promise<unknown>;
  onCreated: (name: string) => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [llm, setLlm] = useState(DEFAULT_MODEL);
  const [blueprint, setBlueprint] = useState('');
  const [models, setModels] = useState<string[]>([DEFAULT_MODEL]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTitle('');
    setLlm(DEFAULT_MODEL);
    setBlueprint(blueprints.length === 1 ? blueprints[0].id : '');
    agentBuilderApi.llmProfiles().then((p) => p.length && setModels(p), () => undefined);
  }, [isOpen, blueprints]);

  const create = async () => {
    setBusy(true);
    try {
      const wf = await agentBuilderApi.createAgent({ title: title.trim(), llm, blueprint: blueprint === CUSTOM ? undefined : blueprint });
      try {
        // Its DOC record (name, tenant, permissions). Without one the journey is not created.
        const bp = blueprints.find((b) => b.id === blueprint);
        await register(wf.settings.name, wf.settings.title, bp ? `${bp.title}: ${bp.description}` : '');
      } catch (e) {
        await agentBuilderApi.deleteAgent(wf.settings.name).catch(() => undefined);
        throw e;
      }
      onOpenChange(false);
      onCreated(wf.settings.name);
    } catch (e) {
      CtyShowToast(checkType.error, (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="2xl">
      <ModalContent>
        <ModalHeader>{t('Create ASJ')}</ModalHeader>
        <ModalBody className="flex flex-col gap-3" data-testid="ab-create-dialog">
          <Input autoFocus label={t('Name')} placeholder={t('e.g. Hypertek email support')} value={title} onValueChange={setTitle} data-testid="ab-create-name" />
          <Select
            label={t('Model')}
            selectedKeys={[llm]}
            onSelectionChange={(keys) => setLlm(String(Array.from(keys)[0] ?? DEFAULT_MODEL))}
            data-testid="ab-create-model"
          >
            {models.map((m) => (
              <SelectItem key={m}>{m === DEFAULT_MODEL ? t('Default model') : m}</SelectItem>
            ))}
          </Select>
          <div className="flex flex-col gap-2">
            <span className="text-sm text-default-600">{t('Blueprint')}</span>
            {/* Any kind of journey (a clinic's appointments, claims, ...): no blueprint's rules apply. */}
            <button
              type="button"
              onClick={() => setBlueprint(CUSTOM)}
              className={`rounded-lg border p-3 text-left ${blueprint === CUSTOM ? 'border-primary bg-primary/10' : 'border-default-200 hover:bg-default-100'}`}
              data-testid="ab-blueprint-custom"
              aria-pressed={blueprint === CUSTOM}
            >
              <div className="font-semibold">{t('Custom journey')}</div>
              <div className="text-xs text-default-600">
                {t('Describe any process in the chat. The builder uses the available actions and marks the tools still to add.')}
              </div>
            </button>
            {blueprints.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setBlueprint(b.id)}
                className={`rounded-lg border p-3 text-left ${blueprint === b.id ? 'border-primary bg-primary/10' : 'border-default-200 hover:bg-default-100'}`}
                data-testid={`ab-blueprint-${b.id}`}
                aria-pressed={blueprint === b.id}
              >
                <div className="font-semibold">{b.title}</div>
                <div className="text-xs text-default-600">{b.description}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {Object.entries(b.triggers).map(([k, v]) => (
                    <Chip key={k} size="sm" variant="flat" color="primary">
                      {v.label || k}
                    </Chip>
                  ))}
                  <Chip size="sm" variant="flat">
                    {t('Starts with {{count}} actions', { count: b.tools.length })}
                  </Chip>
                </div>
              </button>
            ))}
          </div>
        </ModalBody>
        <ModalFooter>
          <CtyButton variant="flat" onPress={() => onOpenChange(false)}>
            {t('Cancel')}
          </CtyButton>
          <CtyButton color="primary" onPress={create} isLoading={busy} isDisabled={!title.trim() || !blueprint} id="ab-create-submit">
            {t('Create')}
          </CtyButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default JourneysPage;
